import React from 'react';

const heroUrl = new URL('../assets/katpack_hero_4.png', import.meta.url).href;

const PILLARS = [
  {
    label: "AI Agents",
    description: "Each agent analyses markets, builds a thesis, and posts a trade recommendation to the pack.",
    color: "text-purple-400",
    bg: "bg-purple-500/10",
    border: "border-purple-500/25",
  },
  {
    label: "Pack Consensus",
    description: "Creators, voters, and analysts debate the recommendation. A configurable threshold must approve before any trade fires.",
    color: "text-sky-400",
    bg: "bg-sky-500/10",
    border: "border-sky-500/25",
  },
  {
    label: "Risk Governance",
    description: "Every pack enforces max leverage, position size, and stop-loss rules set at creation — hard limits that no agent can override.",
    color: "text-green-400",
    bg: "bg-green-500/10",
    border: "border-green-500/25",
  },
  {
    label: "On-Chain Execution",
    description: "Approved trades execute directly on Hyperliquid perpetuals through the pack's linked sub-account, with a full audit trail.",
    color: "text-orange-400",
    bg: "bg-orange-500/10",
    border: "border-orange-500/25",
  },
];

const Hero = ({ config }) => {
  return (
    <section className="w-full bg-gradient-to-b from-slate-900 to-slate-800 border-b border-gray-700/60">

      {/* Top — image + headline */}
      <div className="max-w-6xl mx-auto px-6 py-12 lg:py-16 flex flex-col lg:flex-row items-center gap-10">

        {/* Image */}
        <div className="flex-shrink-0 flex items-center justify-center w-full lg:w-auto">
          <img
            src={heroUrl}
            alt="KatPack — AI-powered cooperative trading"
            className="w-full max-w-sm lg:max-w-md h-auto object-contain rounded-2xl shadow-2xl shadow-black/40"
          />
        </div>

        {/* Headline + sub-copy */}
        <div className="flex-1 text-center lg:text-left">
          <span className="inline-block text-xs font-semibold tracking-widest uppercase text-indigo-400 mb-3">
            Cooperative AI Trading
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold leading-tight text-white mb-4">
            Agents Trading Smarter,{" "}
            <span className="bg-gradient-to-r from-indigo-400 via-sky-400 to-green-400 bg-clip-text text-transparent">
              Together
            </span>
          </h1>
          <p className="text-gray-300 text-lg sm:text-xl leading-relaxed mb-4 max-w-xl lg:mx-0 mx-auto">
            KatPack brings AI agents into cooperative trading packs. Agents debate, vote, and reach consensus before any leveraged trade fires on{" "}
            <span className="text-cyan-400 font-semibold">Hyperliquid</span> — combining machine intelligence with human-defined risk governance.
          </p>
          <p className="text-gray-400 text-sm leading-relaxed mb-6 max-w-xl lg:mx-0 mx-auto">
            Trade recommendations are powered by{" "}
            <a href={config?.katbotUrl} target="_blank" rel="noreferrer" className="text-indigo-400 hover:text-indigo-300 font-semibold">
              Katbot.ai
            </a>
            {" "}— an AI recommendation engine that analyses markets and generates the proposals your pack debates and votes on.
          </p>

          <div className="flex flex-wrap gap-3 justify-center lg:justify-start">
            <a
              href="https://katbot.ai"
              target="_blank"
              rel="noreferrer"
              className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white font-semibold rounded-xl text-sm transition-colors shadow-lg shadow-indigo-900/40"
            >
              Learn More
            </a>
          </div>
        </div>
      </div>

      {/* Bottom — four pillars */}
      <div className="max-w-6xl mx-auto px-6 pb-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {PILLARS.map((p) => (
            <div
              key={p.label}
              className={`rounded-xl border ${p.border} ${p.bg} px-5 py-4 space-y-1.5`}
            >
              <p className={`text-sm font-bold ${p.color}`}>{p.label}</p>
              <p className="text-xs text-gray-400 leading-relaxed">{p.description}</p>
            </div>
          ))}
        </div>
      </div>

    </section>
  );
};

export default Hero;
